import type { ExpressionSpecification, SymbolLayerSpecification } from 'mapbox-gl';
import { FONT_SIZE_100 } from '@alltrails/denali/tokens';
import { defaultPinLayerLayoutProps, defaultSymbolLayerLayoutProps } from './layers';
import {
  isTrailClosedExpression,
  isTrailCollapsedExpression,
  isTrailCompletedExpression,
  isTrailPriorityHighExpression,
  isTrailPriorityLowExpression,
  isTrailPriorityMediumExpression,
  isTrailSavedExpression,
  trailPinCollapsedClosedImageName,
  trailPinCollapsedCompletedImageName,
  trailPinCollapsedImageName,
  trailPinCollapsedPriorityHighImageName,
  trailPinCollapsedPriorityLowImageName,
  trailPinCollapsedPriorityMediumImageName,
  trailPinCollapsedSavedImageName,
  trailPinCollapsedViewedImageName
} from './trailPinConstants';

export function buildCommonLayoutProps(
  isHoveredExpression: ExpressionSpecification,
  isSelectedExpression: ExpressionSpecification
): SymbolLayerSpecification['layout'] {
  return {
    ...defaultSymbolLayerLayoutProps,
    ...defaultPinLayerLayoutProps,
    'symbol-sort-key': ['case', ['any', isHoveredExpression, isSelectedExpression], 1, 0],
    'icon-allow-overlap': true
  };
}

export function buildDynamicTrailPinProps(
  isHoveredExpression: ExpressionSpecification,
  isSelectedExpression: ExpressionSpecification,
  isActiveTrailExpression: ExpressionSpecification
): SymbolLayerSpecification['layout'] {
  return {
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-text-fit': 'both',
    'text-field': ['get', 'lengthDisplay'],
    'text-allow-overlap': true,
    'symbol-sort-key': [
      'case',
      ['any', isHoveredExpression, isActiveTrailExpression],
      500,
      ['any', isSelectedExpression],
      400,
      isTrailPriorityHighExpression,
      ['+', 300, ['coalesce', ['get', 'popularity'], 0]],
      isTrailPriorityMediumExpression,
      ['+', 200, ['coalesce', ['get', 'popularity'], 0]],
      isTrailPriorityLowExpression,
      ['+', 100, ['coalesce', ['get', 'popularity'], 0]],
      ['coalesce', ['get', 'popularity'], 0]
    ],
    'text-font': ['AllTrails Aeonik Medium', 'Manrope Bold'],
    'text-size': FONT_SIZE_100,
    'text-line-height': 1
  };
}

export function buildRegularTrailIconImage(
  isHoveredExpression: ExpressionSpecification,
  isSelectedExpression: ExpressionSpecification,
  isViewedExpression: ExpressionSpecification,
  isActiveTrailExpression: ExpressionSpecification
): ExpressionSpecification {
  const isActiveOrHovered = ['any', isHoveredExpression, isSelectedExpression, isActiveTrailExpression];

  return [
    'case',
    // Priority trails (take precedence over saved/completed/closed)
    ['all', isActiveOrHovered, isTrailPriorityHighExpression],
    'trail-length-pin-stretchable-active-priority-high',
    ['all', isActiveOrHovered, isTrailPriorityMediumExpression],
    'trail-length-pin-stretchable-active-priority-medium',
    ['all', isActiveOrHovered, isTrailPriorityLowExpression],
    'trail-length-pin-stretchable-active-priority-low',
    isTrailPriorityHighExpression,
    'trail-length-pin-stretchable-default-priority-high',
    isTrailPriorityMediumExpression,
    'trail-length-pin-stretchable-default-priority-medium',
    isTrailPriorityLowExpression,
    'trail-length-pin-stretchable-default-priority-low',

    // Closed single trails
    ['all', isActiveOrHovered, isTrailClosedExpression],
    'trail-length-pin-stretchable-active-closed',
    ['all', isViewedExpression, isTrailClosedExpression],
    'trail-length-pin-stretchable-viewed-closed',
    isTrailClosedExpression,
    'trail-length-pin-stretchable-default-closed',

    // Saved single trails
    ['all', isActiveOrHovered, isTrailSavedExpression],
    'trail-length-pin-stretchable-active-saved',
    ['all', isViewedExpression, isTrailSavedExpression],
    'trail-length-pin-stretchable-viewed-saved',
    isTrailSavedExpression,
    'trail-length-pin-stretchable-default-saved',

    // Completed single trails
    ['all', isActiveOrHovered, isTrailCompletedExpression],
    'trail-length-pin-stretchable-active-completed',
    ['all', isViewedExpression, isTrailCompletedExpression],
    'trail-length-pin-stretchable-viewed-completed',
    isTrailCompletedExpression,
    'trail-length-pin-stretchable-default-completed',

    isActiveOrHovered,
    'trail-length-pin-stretchable-active-trail',
    isViewedExpression,
    'trail-length-pin-stretchable-viewed-trail',
    // Default fallback
    'trail-length-pin-stretchable-default-trail'
  ];
}

export function buildCollapsedTrailIconImage(
  isHoveredExpression: ExpressionSpecification,
  isSelectedExpression: ExpressionSpecification,
  isViewedExpression: ExpressionSpecification,
  isActiveTrailExpression: ExpressionSpecification
): ExpressionSpecification {
  const isActiveOrHovered = ['any', isHoveredExpression, isSelectedExpression, isActiveTrailExpression];

  // Icon image expression for collapsed trail pins
  // When hovered/selected/active, show the full stretchable pill-shaped pin
  return [
    'case',

    // Priority collapsed trails (take precedence over saved/completed/closed)
    ['all', isActiveOrHovered, isTrailPriorityHighExpression, isTrailCollapsedExpression],
    'trail-length-pin-stretchable-active-priority-high',
    ['all', isActiveOrHovered, isTrailPriorityMediumExpression, isTrailCollapsedExpression],
    'trail-length-pin-stretchable-active-priority-medium',
    ['all', isActiveOrHovered, isTrailPriorityLowExpression, isTrailCollapsedExpression],
    'trail-length-pin-stretchable-active-priority-low',
    ['all', isTrailPriorityHighExpression, isTrailCollapsedExpression],
    trailPinCollapsedPriorityHighImageName,
    ['all', isTrailPriorityMediumExpression, isTrailCollapsedExpression],
    trailPinCollapsedPriorityMediumImageName,
    ['all', isTrailPriorityLowExpression, isTrailCollapsedExpression],
    trailPinCollapsedPriorityLowImageName,

    // Closed collapsed trails
    ['all', isActiveOrHovered, isTrailClosedExpression, isTrailCollapsedExpression],
    'trail-length-pin-stretchable-active-closed',
    ['all', isViewedExpression, isTrailClosedExpression, isTrailCollapsedExpression],
    trailPinCollapsedClosedImageName,
    ['all', isTrailClosedExpression, isTrailCollapsedExpression],
    trailPinCollapsedClosedImageName,

    // Saved collapsed trails
    ['all', isActiveOrHovered, isTrailSavedExpression, isTrailCollapsedExpression],
    'trail-length-pin-stretchable-active-saved',
    ['all', isViewedExpression, isTrailSavedExpression, isTrailCollapsedExpression],
    trailPinCollapsedSavedImageName,
    ['all', isTrailSavedExpression, isTrailCollapsedExpression],
    trailPinCollapsedSavedImageName,

    // Completed collapsed trails
    ['all', isActiveOrHovered, isTrailCompletedExpression, isTrailCollapsedExpression],
    'trail-length-pin-stretchable-active-completed',
    ['all', isViewedExpression, isTrailCompletedExpression, isTrailCollapsedExpression],
    trailPinCollapsedCompletedImageName,
    ['all', isTrailCompletedExpression, isTrailCollapsedExpression],
    trailPinCollapsedCompletedImageName,

    // When hovered/selected/active, show the regular active pin
    ['any', isActiveOrHovered],
    // Use the stretchable pill-shaped pin when active
    'trail-length-pin-stretchable-active-trail',
    isViewedExpression,
    trailPinCollapsedViewedImageName,

    // Default collapsed
    trailPinCollapsedImageName
  ];
}

export function buildCollapsedPinFilters(
  isHoveredExpression: ExpressionSpecification,
  isSelectedExpression: ExpressionSpecification,
  isActiveTrailExpression: ExpressionSpecification
): {
  isCollapsedInactiveFilter: ExpressionSpecification;
  isCollapsedActiveFilter: ExpressionSpecification;
} {
  // Inactive collapsed (no icon-offset jump when expanding); active collapsed on separate layer with no icon-offset
  const isCollapsedInactiveFilter = [
    'all',
    ['!', ['has', 'point_count']],
    isTrailCollapsedExpression,
    ['!', ['any', isHoveredExpression, isSelectedExpression, isActiveTrailExpression]]
  ] as ExpressionSpecification;
  const isCollapsedActiveFilter = [
    'all',
    ['!', ['has', 'point_count']],
    isTrailCollapsedExpression,
    ['any', isHoveredExpression, isSelectedExpression, isActiveTrailExpression]
  ] as ExpressionSpecification;

  return {
    isCollapsedInactiveFilter,
    isCollapsedActiveFilter
  };
}
